// strv-standard: dialog v1.0.0
// Master copy lives in strvcom/strv-it-hub /standard. Copy unchanged.
//
// Replaces window.confirm() and window.prompt(). Those show a browser banner
// ("loot.strv.io says…"), look off-brand, and are blocked inside a sandboxed
// frame. Built on the native <dialog>, so focus and Escape work for free.

import { el } from './dom.js';

/**
 * Ask a yes/no question.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.body]
 * @param {string} [opts.confirm] label of the confirming button (default "Confirm")
 * @param {string} [opts.cancel] default "Cancel"
 * @param {boolean} [opts.danger] confirming deletes or undoes something
 * @returns {Promise<boolean>}
 */
export function confirmDialog({ title, body = '', confirm = 'Confirm', cancel = 'Cancel', danger = false }) {
  return open({ title, body, confirm, cancel, danger }).then((r) => r !== null);
}

/**
 * Ask for one value.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.body]
 * @param {string} [opts.label] field label
 * @param {string} [opts.value] initial value
 * @param {string} [opts.placeholder]
 * @param {string} [opts.confirm] default "Save"
 * @param {(v: string) => string | null} [opts.validate] return an error message or null
 * @returns {Promise<string | null>} null when cancelled
 */
export function promptDialog({ title, body = '', label = '', value = '', placeholder = '', confirm = 'Save', validate }) {
  return open({ title, body, confirm, cancel: 'Cancel', field: { label, value, placeholder, validate } });
}

function open({ title, body, confirm, cancel, danger = false, field }) {
  return new Promise((resolve) => {
    const input = field
      ? el('input', { class: 'input', value: field.value, placeholder: field.placeholder, 'aria-label': field.label || title })
      : null;
    const error = el('p', { class: 'muted', role: 'alert' });

    const okBtn = el('button', { class: ['btn', danger ? 'btn-danger' : 'btn-primary'], value: 'ok' }, confirm);
    const cancelBtn = el('button', { class: 'btn btn-ghost', value: 'cancel', type: 'button' }, cancel);

    const dialog = el(
      'dialog',
      { class: 'dialog', 'aria-labelledby': 'strv-dialog-title' },
      el(
        'form',
        { method: 'dialog' },
        el(
          'div',
          { class: 'dialog-body' },
          el('h2', { id: 'strv-dialog-title' }, title),
          body ? el('p', { class: 'muted' }, body) : null,
          input
            ? el('label', { class: 'field' }, field.label ? el('span', { class: 'label' }, field.label) : null, input)
            : null,
          input ? error : null,
        ),
        el('div', { class: 'dialog-actions' }, cancelBtn, okBtn),
      ),
    );

    let result = null;
    dialog.querySelector('form').addEventListener('submit', (e) => {
      if (input && field.validate) {
        const message = field.validate(input.value.trim());
        if (message) {
          e.preventDefault();
          error.textContent = message;
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
      }
      result = input ? input.value.trim() : 'ok';
    });
    cancelBtn.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => {
      dialog.remove();
      resolve(result);
    });

    document.body.append(dialog);
    dialog.showModal();
    (input || (danger ? cancelBtn : okBtn)).focus();
  });
}
